import React, { useEffect, useState, useCallback } from 'react';
import { Headphones, Volume2, X } from 'lucide-react';
import { getAudioEngine, AudioEngine } from '../../audio';

export interface GestureUnlockModalProps {
  engine?: AudioEngine;
  onUnlocked?: () => void;
  onDismiss?: () => void;
  forceOpen?: boolean;
}

export const GestureUnlockModal: React.FC<GestureUnlockModalProps> = ({
  engine: propEngine,
  onUnlocked,
  onDismiss,
  forceOpen,
}) => {
  const [engineInstance] = useState<AudioEngine>(() => propEngine || getAudioEngine());
  const [isVisible, setIsVisible] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setIsVisible(true);
      return;
    }

    // Subscribe to engine state
    const unsubscribe = engineInstance.onStateChange((state) => {
      if (!state.isUnlocked) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    });

    return unsubscribe;
  }, [engineInstance, forceOpen]);

  const handleUnlock = useCallback(async () => {
    setIsUnlocking(true);
    try {
      const success = await engineInstance.unlockAudio();
      if (success) {
        setIsVisible(false);
        onUnlocked?.();
      }
    } catch (err) {
      console.warn('[GestureUnlockModal] Unlock error:', err);
    } finally {
      setIsUnlocking(false);
    }
  }, [engineInstance, onUnlocked]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  // Keyboard shortcut (Enter or Space to unlock, Esc to dismiss)
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleUnlock();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleUnlock, handleDismiss]);

  if (!isVisible) return null;

  return (
    <div
      className="caesura-audio-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="audio-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 7, 10, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1.5rem',
      }}
    >
      <div
        className="caesura-audio-modal-card"
        style={{
          width: '100%',
          maxWidth: '460px',
          backgroundColor: 'var(--void-surface, #0c121c)',
          border: '1px solid var(--planck-cyan, #00f3ff)',
          boxShadow: '0 0 30px rgba(0, 243, 255, 0.25), inset 0 0 15px rgba(0, 243, 255, 0.05)',
          borderRadius: '4px',
          padding: '2rem',
          position: 'relative',
          color: 'var(--text-prose, #d8e2ec)',
          fontFamily: 'var(--font-mono, monospace)',
        }}
      >
        {/* Close / Dismiss button */}
        <button
          onClick={handleDismiss}
          className="caesura-modal-close-btn"
          aria-label="Dismiss audio prompt"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted, #6b7c96)',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={18} />
        </button>

        {/* Header telemetry badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            marginBottom: '1rem',
            color: 'var(--planck-cyan, #00f3ff)',
            fontSize: '0.75rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          <Headphones size={16} />
          <span>NEURAL AUDIO BUS // PROTOCOL M4</span>
        </div>

        {/* Title */}
        <h2
          id="audio-modal-title"
          style={{
            margin: '0 0 0.8rem 0',
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'var(--text-heading, #f0f4f8)',
            letterSpacing: '0.04em',
          }}
        >
          ENGAGE AUDITORY TELEMETRY
        </h2>

        {/* Narrative context copy */}
        <p
          style={{
            fontSize: '0.85rem',
            lineHeight: 1.6,
            color: 'var(--text-muted, #6b7c96)',
            marginBottom: '1.5rem',
            fontFamily: 'var(--font-sans, sans-serif)',
          }}
        >
          The Caesura Engine synthesizes a continuous procedural soundscape in real-time
          (40Hz binaural drone, cosmic void hiss, and subharmonic engine pulse).
          Browser autoplay policy requires an initial gesture to initialize the audio graph.
        </p>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={handleUnlock}
            disabled={isUnlocking}
            className="caesura-unlock-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              backgroundColor: 'rgba(0, 243, 255, 0.12)',
              border: '1px solid var(--planck-cyan, #00f3ff)',
              color: 'var(--planck-cyan, #00f3ff)',
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              borderRadius: '2px',
              transition: 'all 0.2s ease',
            }}
          >
            <Volume2 size={16} />
            <span>{isUnlocking ? 'SYNCHRONIZING...' : 'INITIALIZE AUDIO'}</span>
          </button>

          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted, #6b7c96)',
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              padding: '0.4rem',
              textAlign: 'center',
            }}
          >
            PROCEED IN SILENCE
          </button>
        </div>
      </div>
    </div>
  );
};

export default GestureUnlockModal;
